import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { API, WILDER_AVATAR } from "@/lib/api";

function getSessionId() {
  let id = localStorage.getItem("wilder_session");
  if (!id) {
    id = `ws-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("wilder_session", id);
  }
  return id;
}

const WELCOME =
  "Ciao! Sono Wilder, il genio folle uscito dai quadri di Italo. Dimmi: vuoi imparare a cucinare come si dipinge, hai un locale da trasformare in galleria, o sei un creator in cerca di scintille?";

export default function ChatBox({ compact }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    fetch(`${API}/wilder/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((h) => {
        if (Array.isArray(h) && h.length) {
          setMessages([{ role: "assistant", content: WELCOME }, ...h.map((m) => ({ role: m.role, content: m.content }))]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e, preset) => {
    e?.preventDefault();
    const text = (preset ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/wilder/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I miei pennelli si sono inceppati... riprova tra un attimo." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col ${compact ? "h-[min(440px,62vh)]" : "h-[min(560px,72vh)]"} border border-[#2A2A2A] bg-[#0A0A0A]`} data-testid="wilder-chatbox">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={`${m.role}-${i}`} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && (
              <img
                src={WILDER_AVATAR}
                alt="Wilder"
                className={`w-8 h-8 object-cover rounded-full border border-[#D4AF37] shrink-0 wilder-pop ${
                  busy && i === messages.length - 1 ? "wilder-talk" : ""
                }`}
              />
            )}
            <div
              className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                m.role === "user" ? "chat-bubble-user" : "chat-bubble-wilder"
              }`}
              data-testid={`chat-message-${m.role}-${i}`}
            >
              {m.content || <span className="inline-block animate-pulse">Wilder sta dipingendo la risposta…</span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {messages.length < 3 && (
        <div className="px-3 pb-2 flex flex-wrap gap-2" data-testid="chat-suggestions">
          {[
            "Consigliami l'opera giusta per me",
            "Che piatto fa per i miei gusti?",
            "Abbinami un quadro e un piatto",
          ].map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy}
              onClick={() => send(null, s)}
              className="text-xs border border-[#D4AF37] text-[#E4BD44] px-3 py-1.5 hover:bg-[#D4AF37]/10 transition-colors"
              data-testid={`chat-chip-${s.slice(0, 10)}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={send} className="border-t border-[#2A2A2A] p-3 flex gap-2 bg-[#141414]">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Scrivi a Wilder…"
          className="ws-input flex-1 !py-2.5 bg-[#0A0A0A]"
          data-testid="chat-input"
        />
        <button type="submit" disabled={busy} className="btn-primary !px-4 !py-2.5" data-testid="chat-send-button" aria-label="Invia">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}
